import {comparer, reaction, toJS} from 'mobx';
import {IBiReportingParams} from '../interfaces/IReportingParams';

// Catalogs can contain thousands of options. History retains only report settings.
function snapshot(params: IBiReportingParams): IBiReportingParams | null {
    if (!params) return null;
    const {filters = [], ...settings} = params;
    return {
        ...Object.fromEntries(Object.entries(settings).map(([key, value]) => [key, toJS(value)])),
        filters: filters.map(({filterList, isLoading, ...filter}) => ({
            ...filter, values: toJS(filter.values), filterList: [], isLoading: false,
        })),
    } as IBiReportingParams;
}

export default class ReportingParamsHistory {
    private past: IBiReportingParams[] = [];
    private future: IBiReportingParams[] = [];
    private current: IBiReportingParams | null = null;
    private pending: IBiReportingParams | null = null;
    private scheduled = false;
    private restoring = false;
    public dispose: () => void;

    constructor(private read: () => IBiReportingParams, private restore: (params: IBiReportingParams) => void) {
        this.current = snapshot(read());
        this.dispose = reaction(() => snapshot(read()), next => {
            if (this.restoring) return;
            this.pending = next;
            if (this.scheduled) return;
            this.scheduled = true;
            // One synchronous user action (e.g. a date preset) is one undo step.
            Promise.resolve().then(() => {
                this.scheduled = false;
                this.commit();
            });
        }, {equals: comparer.structural});
    }

    public reset = () => {
        this.past = [];
        this.future = [];
        this.pending = null;
        this.current = snapshot(this.read());
    };

    private commit() {
        const next = this.pending;
        this.pending = null;
        if (!next || comparer.structural(next, this.current)) return;
        if (this.current) this.past.push(this.current);
        if (this.past.length > 100) this.past.shift();
        this.current = next;
        this.future = [];
    }

    public undo = () => this.move(false);
    public redo = () => this.move(true);

    private move(forward: boolean): boolean {
        this.commit();
        const from = forward ? this.future : this.past;
        const to = forward ? this.past : this.future;
        const next = from.pop();
        if (!next) return false;
        if (this.current) to.push(this.current);
        this.current = next;
        this.restoring = true;
        try {
            this.restore(next);
        } finally {
            this.restoring = false;
        }
        return true;
    }
}
